<template>
  <div class="message-list">
    <!-- Load More Button -->
    <div v-if="hasMoreMessages && !isLoading" class="load-more-container">
      <button
        @click="$emit('load-more')"
        class="load-more-button"
        :disabled="isLoadingMore"
      >
        <span v-if="isLoadingMore">Loading...</span>
        <span v-else>Load older messages</span>
      </button>
    </div>

    <!-- Date Separator -->
    <div v-for="(group, date) in groupedMessages" :key="date" class="message-group">
      <div class="date-separator">
        <span class="date-text">{{ formatDate(date) }}</span>
      </div>

      <!-- Messages -->
      <div
        v-for="message in group"
        :key="message.id"
        class="message-wrapper"
        :class="messageWrapperClasses(message)"
      >
        <MessageBubble
          :message="message"
          :current-user="currentUser"
          :show-avatar="shouldShowAvatar(message, group, group.indexOf(message))"
          :show-timestamp="shouldShowTimestamp(message, group, group.indexOf(message))"
          @action="$emit('message-action', $event.action, $event.message)"
        />
      </div>
    </div>

    <!-- Loading Indicator -->
    <div v-if="isLoading" class="loading-indicator">
      <div class="typing-dots">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import MessageBubble from './MessageBubble.vue'

export default {
  name: 'MessageList',
  components: {
    MessageBubble
  },
  props: {
    messages: {
      type: Array,
      required: true
    },
    currentUser: {
      type: Object,
      default: () => ({})
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    hasMoreMessages: {
      type: Boolean,
      default: false
    }
  },
  emits: ['load-more', 'message-action'],
  setup(props) {
    const isLoadingMore = ref(false)

    // Grouper les messages par date
    const groupedMessages = computed(() => {
      const groups = {}

      props.messages.forEach(message => {
        const date = new Date(message.timestamp).toDateString()
        if (!groups[date]) {
          groups[date] = []
        }
        groups[date].push(message)
      })

      return groups
    })

    // Classes CSS pour le wrapper de message
    const messageWrapperClasses = (message) => ({
      'message-wrapper--own': message.userId === props.currentUser?.id,
      'message-wrapper--other': message.userId !== props.currentUser?.id,
      'message-wrapper--first': isFirstInGroup(message),
      'message-wrapper--last': isLastInGroup(message),
      'message-wrapper--system': message.type === 'system'
    })

    // Vérifier si c'est le premier message d'un groupe
    const isFirstInGroup = (message) => {
      const userMessages = props.messages.filter(m => m.userId === message.userId)
      return userMessages[0]?.id === message.id
    }

    // Vérifier si c'est le dernier message d'un groupe
    const isLastInGroup = (message) => {
      const userMessages = props.messages.filter(m => m.userId === message.userId)
      return userMessages[userMessages.length - 1]?.id === message.id
    }

    // Déterminer si afficher l'avatar
    const shouldShowAvatar = (message, group, index) => {
      if (message.userId === props.currentUser?.id) return false
      if (message.type === 'system') return false

      // Afficher l'avatar pour le premier message d'un groupe consécutif
      if (index === 0) return true

      const previousMessage = group[index - 1]
      return previousMessage.userId !== message.userId
    }

    // Déterminer si afficher l'horodatage
    const shouldShowTimestamp = (message, group, index) => {
      // Toujours afficher pour les messages système
      if (message.type === 'system') return true

      // Afficher pour le premier message
      if (index === 0) return true

      // Afficher si plus de 5 minutes se sont écoulées depuis le message précédent
      const previousMessage = group[index - 1]
      const timeDiff = new Date(message.timestamp) - new Date(previousMessage.timestamp)
      return timeDiff > 5 * 60 * 1000 // 5 minutes
    }

    // Formater la date
    const formatDate = (dateString) => {
      const date = new Date(dateString)
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      if (date.toDateString() === today.toDateString()) {
        return 'Today'
      } else if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday'
      } else {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
        })
      }
    }

    // Gérer le chargement de messages supplémentaires
    const handleLoadMore = async () => {
      isLoadingMore.value = true
      try {
        await new Promise(resolve => setTimeout(resolve, 1000)) // Simulation
        // L'événement sera émis par le composant parent
      } finally {
        isLoadingMore.value = false
      }
    }

    return {
      isLoadingMore,
      groupedMessages,
      messageWrapperClasses,
      shouldShowAvatar,
      shouldShowTimestamp,
      formatDate,
      handleLoadMore
    }
  }
}
</script>

<style scoped>
.message-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 100%;
}

.load-more-container {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
}

.load-more-button {
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 20px;
  color: #6b7280;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.load-more-button:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}

.load-more-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.message-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.date-separator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem 0;
  position: relative;
}

.date-separator::before,
.date-separator::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e5e7eb;
}

.date-text {
  padding: 0.25rem 0.75rem;
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 12px;
  margin: 0 1rem;
  white-space: nowrap;
}

.message-wrapper {
  display: flex;
  margin-bottom: 0.25rem;
}

.message-wrapper--own {
  justify-content: flex-end;
}

.message-wrapper--other {
  justify-content: flex-start;
}

.message-wrapper--system {
  justify-content: center;
}

.message-wrapper--first {
  margin-top: 1rem;
}

.message-wrapper--last {
  margin-bottom: 0.75rem;
}

.loading-indicator {
  display: flex;
  justify-content: center;
  padding: 1rem;
}

.typing-dots {
  display: flex;
  gap: 4px;
  padding: 0.75rem 1rem;
  background: #e5e7eb;
  border-radius: 20px;
}

.typing-dots span {
  width: 8px;
  height: 8px;
  background: #6b7280;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 640px) {
  .message-wrapper {
    margin-bottom: 0.125rem;
  }

  .message-wrapper--first {
    margin-top: 0.75rem;
  }

  .message-wrapper--last {
    margin-bottom: 0.5rem;
  }

  .date-text {
    font-size: 0.7rem;
    padding: 0.2rem 0.6rem;
  }
}
</style>